import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Landing from './pages/Landing';
import DeploymentPage from './pages/landing/DeploymentPage';
import SecurityPage from './pages/landing/SecurityPage';
import GatewayPage from './pages/landing/GatewayPage';
import NotFound from './pages/NotFound';
import useBrandTheme from './brand/useBrandTheme';

// The public docs are their own chunk, loaded only when someone opens /docs.
const DocsApp = lazy(() => import('./pages/docs/DocsApp'));

// Holds the paper theme while the docs chunk loads. With no page mounted, the
// marketing page's scope would drop and the console theme would flash in between.
function PaperFallback() {
  useBrandTheme({ paperOnly: true });
  return null;
}

// The hosted agents console is a chunk of its own as well: the landing page, the docs and
// the prerender never load it.
const Agents = lazy(() => import('./pages/Agents'));

const agentsPage = (tab) => (
  <Suspense fallback={<div className="p-6 text-sm text-fg-muted">Loading…</div>}>
    <Agents tab={tab} />
  </Suspense>
);

import Login from './pages/Login';
import Signup from './pages/Signup';
import AcceptInvite from './pages/AcceptInvite';
import Organizations from './pages/Organizations';
import Members from './pages/Members';
import ForgotPassword from './pages/ForgotPassword';
import Users from './pages/Users';
import LLMStats from './pages/LLMStats';
import Settings from './pages/Settings';
import Account from './pages/Account';
import Tiers from './pages/Tiers';
import Projects from './pages/Projects';
import Guardrails from './pages/Guardrails';
import PromptManagement from './pages/PromptManagement';
import HarnessEval from './pages/HarnessEval';
import MyProjects from './pages/MyProjects';
import MyUsage from './pages/MyUsage';
import Alerts from './pages/Alerts';
import SharedSessions from './pages/SharedSessions';
import SessionRegistry from './pages/SessionRegistry';
import Feedback from './pages/Feedback';
import FeedbackInbox from './pages/FeedbackInbox';
import CreditRequests from './pages/CreditRequests';
import Dashboard from './pages/Dashboard';
import AgentSettings from './pages/AgentSettings';
import GitHubSetup from './pages/GitHubSetup';
import Layout from './components/Layout';
import { OrgProvider, useOrg } from './contexts/OrgContext';
import { dashboardPath } from './auth';
import PageMeta from './seo/PageMeta';

function PrivateRoute({ children }) {
  const token = localStorage.getItem('admin_token');
  return token ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const isAdmin = localStorage.getItem('admin_is_admin') === 'true';
  return isAdmin ? children : <Navigate to="/account" replace />;
}

// Org-scoped admin pages. Membership in the ACTIVE org decides — `is_admin` is
// install-wide and opens nothing here, matching what the API enforces.
function OrgManagerRoute({ children }) {
  // Read through the provider rather than localStorage, so the decision can't
  // race the fetch that populates it.
  const { isManager } = useOrg();
  return isManager ? children : <Navigate to="/organizations" replace />;
}

// `/` is public and always renders the landing page — for signed-in users too.
// They get a "Dashboard" button there (see Landing) instead of being redirected,
// so the header logo can reliably bring anyone back to the install/docs surface.
function PublicHome() {
  return <Landing />;
}

// Auth pages: an already-logged-in user should never see them. Sending them to
// the dashboard (replace) is also what makes the browser Back button behave —
// pressing Back onto /login after signing in bounces straight to the app.
function GuestRoute({ children }) {
  const token = localStorage.getItem('admin_token');
  if (token) return <Navigate to={dashboardPath()} replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <PageMeta />
      <Routes>
        {/* Public */}
        <Route path="/" element={<PublicHome />} />
        <Route path="/deployment" element={<DeploymentPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/latentgateway" element={<GatewayPage />} />
        <Route path="/docs/*" element={<Suspense fallback={<PaperFallback />}><DocsApp /></Suspense>} />
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/signup" element={<GuestRoute><Signup /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        {/* Accept-invite works signed-in or not (the page handles both). */}
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Authenticated app (path-less layout route) */}
        <Route
          element={
            <PrivateRoute>
              <OrgProvider>
                <Layout />
              </OrgProvider>
            </PrivateRoute>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="account" element={<Account />} />
          <Route path="my-projects" element={<MyProjects />} />
          <Route path="my-usage" element={<MyUsage />} />
          <Route path="agent-settings" element={<AgentSettings />} />
          <Route path="github/setup" element={<GitHubSetup />} />
          <Route path="agents" element={agentsPage()} />
          {/* Static paths outrank `:sessionId`, and session ids start with ags_, so neither hides the other. */}
          <Route path="agents/new" element={agentsPage('new')} />
          <Route path="agents/webhooks" element={agentsPage('webhooks')} />
          <Route path="agents/schedules" element={agentsPage('schedules')} />
          <Route path="agents/repositories" element={agentsPage('repositories')} />
          <Route path="agents/:sessionId" element={agentsPage()} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="members" element={<OrgManagerRoute><Members /></OrgManagerRoute>} />
          <Route path="alerts" element={<Alerts />} />
          <Route path="shared-sessions" element={<SharedSessions />} />
          <Route path="feedback" element={<Feedback />} />
          <Route path="tiers" element={<OrgManagerRoute><Tiers /></OrgManagerRoute>} />
          <Route path="projects" element={<OrgManagerRoute><Projects /></OrgManagerRoute>} />
          <Route path="guardrails" element={<OrgManagerRoute><Guardrails /></OrgManagerRoute>} />
          <Route path="prompts" element={<OrgManagerRoute><PromptManagement /></OrgManagerRoute>} />
          <Route path="harness-eval" element={<OrgManagerRoute><HarnessEval /></OrgManagerRoute>} />
          <Route path="users" element={<OrgManagerRoute><Users /></OrgManagerRoute>} />
          <Route path="users/:userId/usage" element={<OrgManagerRoute><MyUsage /></OrgManagerRoute>} />
          <Route path="llm-stats" element={<OrgManagerRoute><LLMStats /></OrgManagerRoute>} />
          <Route path="settings" element={<OrgManagerRoute><Settings /></OrgManagerRoute>} />
          <Route path="session-registry" element={<OrgManagerRoute><SessionRegistry /></OrgManagerRoute>} />
          <Route path="credit-requests" element={<OrgManagerRoute><CreditRequests /></OrgManagerRoute>} />
          <Route path="feedback-inbox" element={<OrgManagerRoute><FeedbackInbox /></OrgManagerRoute>} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
